/**
 * Сохранение и загрузка профиля (ProfileState) с versioning и миграциями.
 */

import type { ProfileState } from '../state/Profile';
import {
  createDefaultProfileState,
  deriveCollectedCount,
} from '../state/Profile';
import { getJSON, setJSON, remove } from './Storage';

const STORAGE_KEY = 'one-day-puzzle.profile';

const CURRENT_VERSION = 1;

type Migration = (state: Record<string, unknown>) => Record<string, unknown>;

/** Миграции: индекс = с какой версии применять (0 = нет version в данных). Пока пусто. */
const MIGRATIONS: Migration[] = [
  (_) => _, // 0 -> 1 (no-op, default уже совместим)
  // future: (state) => ({ ...state, version: 2, newField: [] }),
];

function migrate(state: Record<string, unknown>): ProfileState {
  const rawVersion = state.version;
  const fromVersion =
    typeof rawVersion === 'number' && rawVersion >= 0 ? rawVersion : 0;

  for (let v = fromVersion; v < CURRENT_VERSION; v++) {
    const fn = MIGRATIONS[v];
    if (fn) state = fn(state) as Record<string, unknown>;
  }

  const def = createDefaultProfileState();
  return { ...def, ...state, version: CURRENT_VERSION } as unknown as ProfileState;
}

export function load(): ProfileState {
  const raw = getJSON<Record<string, unknown>>(STORAGE_KEY);
  if (!raw || typeof raw !== 'object') {
    return createDefaultProfileState();
  }
  const version = raw.version;
  if (typeof version !== 'number' || version < CURRENT_VERSION) {
    return migrate(raw);
  }
  return raw as unknown as ProfileState;
}

export function save(state: ProfileState): void {
  const toSave = { ...state, version: CURRENT_VERSION };
  setJSON(STORAGE_KEY, toSave);
}

export function reset(): void {
  remove(STORAGE_KEY);
}

/**
 * Smoke: сохраняет тестовый профиль, загружает, проверяет coins, сбрасывает.
 * Вызвать вручную из консоли: import('./services/ProfileStorage').then(m => m.debugSmoke()).
 */
export function debugSmoke(): void {
  const defaultState = createDefaultProfileState();
  save(defaultState);
  const loaded = load();
  if (loaded.coins !== defaultState.coins || loaded.version !== 1) {
    console.error('ProfileStorage smoke: load after save mismatch', {
      expected: defaultState,
      got: loaded,
    });
    return;
  }
  const withCoins = { ...defaultState, coins: 42 };
  save(withCoins);
  const loaded2 = load();
  if (loaded2.coins !== 42) {
    console.error('ProfileStorage smoke: coins not persisted', loaded2);
    return;
  }
  reset();
  const afterReset = load();
  const def = createDefaultProfileState();
  if (afterReset.coins !== def.coins || Object.keys(afterReset.collectedPuzzleImages).length !== 0) {
    console.error('ProfileStorage smoke: reset did not clear', afterReset);
    return;
  }
  const derived = deriveCollectedCount(afterReset);
  if (derived !== 0) {
    console.error('ProfileStorage smoke: deriveCollectedCount', { derived });
    return;
  }
  console.log('ProfileStorage smoke: ok');
}
